import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Truck, Mail, AlertCircle } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common/States";
import { Can } from "@/components/common/Can";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, NumberField } from "@/components/common/Fields";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EMPTY = { name: "", vat: "", address: "", city: "", email: "", phone: "", lead_days: 2, min_order: 0, notes: "", is_example: false, active: true };

export function SupplierDialog({ open, onOpenChange, supplier, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (open) setForm(supplier ? { ...EMPTY, ...supplier } : EMPTY); }, [open, supplier]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, lead_days: Number(form.lead_days), min_order: Number(form.min_order), is_example: supplier ? form.is_example && !form.email ? form.is_example : false : false };
      const r = supplier ? await api.put(`/suppliers/${supplier.id}`, payload) : await api.post("/suppliers", payload);
      toast.success("Fornitore salvato"); onOpenChange(false); onSaved(r.data);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="supplier-dialog">
        <DialogHeader><DialogTitle>{supplier ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle><DialogDescription>L'email ordini è necessaria per inviare gli ordini dal gestionale.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><TextField label="Ragione sociale" value={form.name} onChange={set("name")} required testId="supplier-name-input" /></div>
          <TextField label="Partita IVA" value={form.vat} onChange={set("vat")} testId="supplier-vat-input" />
          <TextField label="Email ordini" type="email" value={form.email} onChange={set("email")} testId="supplier-email-input" />
          <TextField label="Indirizzo" value={form.address} onChange={set("address")} testId="supplier-address-input" />
          <TextField label="Città" value={form.city} onChange={set("city")} testId="supplier-city-input" />
          <TextField label="Telefono" value={form.phone} onChange={set("phone")} testId="supplier-phone-input" />
          <NumberField label="Giorni di consegna" value={form.lead_days} onChange={set("lead_days")} min={0} step="1" testId="supplier-lead-input" />
          <NumberField label="Minimo d'ordine (€)" value={form.min_order} onChange={set("min_order")} min={0} testId="supplier-min-input" />
          <div className="sm:col-span-2"><Textarea placeholder="Note (condizioni, giorni di consegna…)" value={form.notes} onChange={(e) => set("notes")(e.target.value)} className="bg-white" data-testid="supplier-notes-input" /></div>
          <div className="flex justify-end gap-2 sm:col-span-2"><Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button><Button type="submit" data-testid="supplier-submit">Salva</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function SuppliersPage() {
  const { data, loading, error, reload } = useFetch(() => api.get("/suppliers").then((r) => r.data), []);
  const [open, setOpen] = useState(false);
  return (
    <div className="fade-up" data-testid="suppliers-page">
      <PageHeader title="Fornitori e listini" subtitle="Anagrafiche, condizioni e prezzi per collo. I fornitori marcati «esempio» vanno sostituiti con i tuoi contatti reali."
        actions={<Can perm="suppliers.write" onClick={() => setOpen(true)} data-testid="new-supplier-button"><Plus className="h-4 w-4" /> Nuovo fornitore</Can>} />
      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={reload} /> : data.length === 0 ? <EmptyState icon={Truck} title="Nessun fornitore" description="Aggiungi i fornitori e i loro listini: le proposte d'ordine sceglieranno automaticamente il prezzo migliore." /> : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.map((s) => (
            <li key={s.id} className="card p-4 transition-shadow hover:shadow-md" data-testid={`supplier-card-${s.id}`}>
              <Link to={`/fornitori/${s.id}`} className="block">
                <div className="flex items-start justify-between gap-2"><div className="font-semibold leading-tight">{s.name}</div>{s.is_example && <span className="badge badge-grey shrink-0">Esempio</span>}</div>
                <div className="mt-1 text-xs text-slate-500">{s.city}{s.vat ? ` · P.IVA ${s.vat}` : ""}</div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                  <span className="badge badge-primary">{s.items_count} articoli a listino</span>
                  <span>Consegna {s.lead_days} gg</span>
                  {s.email ? <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</span> : <span className="flex items-center gap-1 text-[#B45309]"><AlertCircle className="h-3 w-3" /> Email ordini mancante</span>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <SupplierDialog open={open} onOpenChange={setOpen} onSaved={() => reload(true)} />
    </div>
  );
}
